/**
 * R2 · 单帧创建页**右回显列**(设计稿 r2-create-split)。
 *
 * ═══ 为什么是「回显」不是「预览」═══════════════════════════════════════════════
 * 长图那条路的右列画得出版式,是因为长图**有区块**——每块的形制与顺序就是版式本身。
 * 单帧没有区块:画面从头到尾由引擎画,前端手里只有「参数」。所以画幅框里明写
 * 「画面由引擎生成,这里不预演」——画一个假版式看着更"专业",但那是撒谎:用户会拿它
 * 对成品,对不上就是我们骗了他。
 *
 * ═══ 2026-07-30 重做:这一列原本没什么意义 ═══════════════════════════════════
 * 改之前它有三块:画幅框 / 画风+配色+字体回显 / 费用摘要。后两块**是左边那几段的
 * 复述** —— 画风在左边有缩略图、配色在左边有色板、费用在底栏也有。同一件事说两遍
 * 不增加信息,用户扫一眼就学会了忽略这一列(用户原话:「右边的数据显示的还没什么意义」)。
 *
 * 现在这一列的主体换成**「这次会发给引擎的指令」实时分段预览**:
 *   - 它是这一页唯一一处能看到「左边那些控件到底变成了什么话」的地方;
 *   - 渲染的是 `buildRawPrompt` 的真实输出(与提交同一个纯函数,不另拼一份摘要 ——
 *     写第二份必漂,而漂移的后果是用户按预览做决定、实际发出去的是另一串);
 *   - 左边改文案 / 换产品 / 换画风 / 换配色 / 换画幅,右边这些段当场变 —— 这就是联动。
 * 画幅框留着(它是唯一「看得见形状」的信息),回显与费用压成一行。
 *
 * 与长图右列同一套纪律:`sticky top-4` + `self-start`(grid 项默认 stretch,拉满高度
 * 的项 sticky 不生效——V2 那次踩过)。
 */
import { useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";

import { cn } from "@/shared/lib/utils";
import { CONSISTENCY_NOTE, type ConsistencyKind } from "../../../model/free-create/consistencyNote";
import type { StyleTokens } from "../../../longimage/model/canvas";

/** 比例 → 画幅框的 aspect-ratio(与 ratioOptions 五档同集合;未知值回落 3:4)。 */
const RATIO_ASPECT: Record<string, string> = {
  "1:1": "1 / 1",
  "4:5": "4 / 5",
  "3:4": "3 / 4",
  "9:16": "9 / 16",
  "16:9": "16 / 9",
};

interface Props {
  /** 生效画幅文案(与底栏同源;可能是「智能」「跟随原图」等非枚举值)。 */
  ratioLabel: string;
  /** 已确定的比例枚举(手选或跟随原图量出来的);null = 还没定,画幅框走 3:4 占位。 */
  ratio: string | null;
  /** 张数:组图/套图会 >1,画幅框叠张数标。 */
  count: number;
  /** 组图/套图模式名(用于画幅框角标说明这是 N 张里的一张)。 */
  batchLabel: string | null;
  /**
   * 2026-07-28 P4 —— 形态预演。给了它就按**每张的真实画幅**并排画占位框,
   * 而不是一个「这里不预演」的空框。
   *   组图 → 每张同比例,框下写对应爆点标题
   *   套图 → 各按自己的比例(9:16 窄 / 1:1 方 / 16:9 宽)
   * 空数组 = 单张,走原来的单框。
   */
  previewCells?: Array<{
    ratio: string | null;
    caption?: string;
    /** 框上角标(B1-4:组图第 1 张标「封面」——它在轮播里的职责与内页不同)。 */
    badge?: string;
  }>;
  /**
   * 「这一组凭什么像一套」(B1-4)。null = 不显示(单张 / 多张同款)。
   *
   * ★版式锚那句话写在 prompt 里,用户看不见 —— 界面不说他不知道我们锁了什么。
   *   但**锚在挂了原图 / 关掉开关时根本不生效**,那时候还写「全组同版式」就是撒谎,
   *   所以判定走 `consistencyKind`(输入是提交时真会发的那几个值),不在这里另算。
   */
  consistency?: ConsistencyKind | null;
  styleLabel: string;
  styleThumbUrl: string | null;
  styleTokens: StyleTokens | null;
  followStyle: boolean;
  fontLabel: string;
  logoOn: boolean;
  qrOn: boolean;
  brandColorsOn: boolean;
  providerLabel: string;
  tierLabel: string;
  /**
   * 「这次会发给引擎的指令」分段预览(`FinalPromptFold`,在这一列默认展开)。
   *
   * ★为什么由调用方装配好传进来,而不是这一列自己拼:那个组件要 8 个表单字段 +
   *   一个回调(「改成我自己写」要写回左边的文案框)。让回显列去接这些,等于把它变成
   *   第二个表单容器;传一个已装配好的节点进来,这一列仍然只负责「怎么摆」。
   */
  promptPreview?: ReactNode;
}

export function CreateMirrorColumn({
  ratioLabel,
  ratio,
  count,
  batchLabel,
  previewCells = [],
  consistency = null,
  styleLabel,
  styleThumbUrl,
  styleTokens,
  followStyle,
  fontLabel,
  logoOn,
  qrOn,
  brandColorsOn,
  providerLabel,
  tierLabel,
  promptPreview,
}: Props) {
  const palette = styleTokens?.palette;
  const brandBits = [logoOn && "LOGO", qrOn && "二维码", brandColorsOn && "品牌色"].filter(
    Boolean
  ) as string[];
  return (
    <div className="sticky top-4 self-start space-y-3" data-create-mirror={ratio ?? "auto"}>
      {/* ① 画幅框:按比例实时变形 + 品牌位示意 */}
      <div className="rounded-[calc(var(--radius)*1.4)] border border-border/60 bg-background/30 p-4">
        {/* P4 形态预演:多张时把「会出几张、各是什么画幅」画出来 ——
            这一列原本 400px 宽只写三行文字 + 一个「这里不预演」的空框。 */}
        {previewCells.length > 1 ? (
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-end justify-center gap-2">
              {previewCells.map((c, i) => (
                <div key={i} className="flex flex-col items-center gap-1" style={{ width: 76 }}>
                  <div
                    style={{ aspectRatio: RATIO_ASPECT[c.ratio ?? ""] ?? RATIO_ASPECT["3:4"] }}
                    className="relative w-full rounded-[calc(var(--radius)*0.8)] border border-primary/40 bg-gradient-to-b from-primary/5 to-background"
                  >
                    {/* B1-4:封面标 —— 轮播第 1 张的职责与内页不同,这一列得看得出来 */}
                    {c.badge && (
                      <span className="absolute left-1/2 top-1 -translate-x-1/2 rounded-full bg-primary px-1.5 py-px text-2xs font-semibold text-primary-foreground">
                        {c.badge}
                      </span>
                    )}
                  </div>
                  <span className="w-full truncate text-center text-2xs text-muted-foreground">
                    {c.caption ?? c.ratio ?? ""}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-center text-[12.5px] text-muted-foreground">
              共 {previewCells.length} 张{batchLabel ? ` · ${batchLabel}` : ""}
              {logoOn || qrOn ? " · 每张都叠品牌位" : ""}
            </p>
          </div>
        ) : (
          <div className="mx-auto flex max-w-[260px] flex-col items-center gap-2.5">
            <div
              data-mirror-frame
              style={{ aspectRatio: RATIO_ASPECT[ratio ?? ""] ?? RATIO_ASPECT["3:4"] }}
              className="relative w-full rounded-[calc(var(--radius)*1.1)] border border-primary/40 bg-gradient-to-b from-primary/5 to-background"
            >
              <div className="flex h-full items-center justify-center px-4 text-center text-[12.5px] leading-relaxed text-muted-foreground">
                画面由引擎生成
                <br />
                这里不预演
              </div>
              {logoOn && (
                <span className="absolute left-2 top-2 rounded-[calc(var(--radius)*0.4)] border border-dashed border-primary/60 bg-primary/10 px-1.5 py-0.5 text-2xs font-medium text-primary">
                  LOGO
                </span>
              )}
              {qrOn && (
                <span className="absolute bottom-2 right-2 grid h-9 w-9 place-items-center rounded-[calc(var(--radius)*0.4)] border border-dashed border-primary/60 bg-primary/10 text-2xs font-medium text-primary">
                  QR
                </span>
              )}
              {count > 1 && (
                <span className="absolute right-2 top-2 rounded-full bg-foreground/80 px-2 py-0.5 text-2xs font-semibold text-background">
                  共 {count} 张{batchLabel ? ` · ${batchLabel}` : ""}
                </span>
              )}
            </div>
            <div className="text-center text-[12.5px] text-muted-foreground">
              画幅 <b className="font-semibold text-foreground">{ratioLabel}</b>
              <br />
              {/* ★「本次不叠」这句留着:两个开关都关时不说话,用户分不清"不叠"与
                  "忘了显示"。可见的失败优于隐形降级,可见的"没有"同理。 */}
              {brandBits.length > 0
                ? `品牌:${brandBits.join(" · ")}(出图时叠加到画面上)`
                : "本次不叠品牌 LOGO / 二维码"}
            </div>
          </div>
        )}
      </div>

      {/* ①.5 这一组凭什么像一套(B1-4)—— 只在多张时出现 */}
      {consistency && <ConsistencyRow kind={consistency} />}

      {/* ② 设置一行 —— 画风缩略图 + 色板 + 字体 + 档位 + 积分,全压进一行。
          ★压成一行是这次重做的一半:这些东西左边全都有,右列复述不增加信息,
            只会把真正有用的指令预览挤到屏幕外面去。 */}
      <div className="flex items-center gap-2.5 rounded-[calc(var(--radius)*1.4)] border border-border/60 bg-card/40 px-3 py-2.5">
        <span
          className={cn(
            "h-9 w-12 shrink-0 overflow-hidden rounded-[calc(var(--radius)*0.7)] border border-border/60 bg-muted",
            !styleThumbUrl && "grid place-items-center text-2xs text-muted-foreground"
          )}
        >
          {styleThumbUrl ? (
            <img src={styleThumbUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            "AI 定"
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-[13px] font-semibold">{styleLabel}</span>
            {palette && (
              <span className="flex shrink-0 gap-1">
                {(["primary", "secondary", "accent", "ink", "bg"] as const).map((k) => (
                  <i
                    key={k}
                    aria-hidden
                    style={{ background: palette[k] }}
                    className="h-3.5 w-3.5 rounded-[3px] border border-border/50"
                  />
                ))}
              </span>
            )}
          </div>
          <div className="truncate text-[12px] text-muted-foreground">
            {palette ? (followStyle ? "配色跟随画风" : "配色已自调") : "配色交给 AI 自定"} · 字体
            {fontLabel} · {count} 张 · {tierLabel} · {providerLabel}
          </div>
        </div>
        {/* ★2026-07-31 这里**不再重复报价**。
            同一笔钱原本说两遍:这条摘要卡一个「280 积分」,底栏还有一个「预计扣 280 积分」。
            留底栏那个 —— 它挨着出图按钮(掏钱的那一下手指就在那儿),而且「预计扣」三个字
            把语义说全了;这里光一个「280 积分」反而含义模糊(是余额?是单价?)。
            本列的职责是**回显你选了什么**,不是报价。 */}
      </div>

      {/* ③ 这次会发给引擎的指令 —— 本列主体,随左边即时变 */}
      {promptPreview}
    </div>
  );
}

/**
 * 一致性承诺行(B1-4)。折叠态一句话,点开列出**具体锁住了什么**。
 *
 * ★为什么要能点开:「全组同版式」这五个字用户会有自己的理解(他可能以为连文字都不变)。
 *   把版式/配色/光线/镜头距离/标题位置/留白逐项写出来,他才知道边界在哪 ——
 *   也才知道"换的是场景与大字"这件事是我们承诺的,不是碰运气。
 */
function ConsistencyRow({ kind }: { kind: ConsistencyKind }) {
  const [open, setOpen] = useState(false);
  const note = CONSISTENCY_NOTE[kind];
  const weak = kind === "off";
  return (
    <div
      className={cn(
        "rounded-[calc(var(--radius)*1.4)] border bg-card/40 px-3 py-2",
        weak ? "border-border/60" : "border-primary/30"
      )}
      data-consistency={kind}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-1.5 text-left"
      >
        <span
          className={cn(
            "text-[12.5px] font-semibold",
            weak ? "text-muted-foreground" : "text-primary"
          )}
        >
          {note.label}
        </span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180"
          )}
        />
      </button>
      {open && (
        <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">{note.detail}</p>
      )}
    </div>
  );
}
